import type { MetaPartner } from '@libs/trip-planner-interfaces';

import { getAffiliateCookieData } from '../affiliateCookie';

/**
 * Sembo affiliate ids per meta-search partner, taken from the "Meta Agency IDs"
 * sheet (https://docs.google.com/spreadsheets/d/1Zh_xB26ZsJfGLZ3zjz8lwVQV-cRplcsZKndfM6y2f-M/edit?gid=0#gid=0).
 *
 * To onboard a new partner: extend `MetaPartner`, add its ids here, its label in
 * `META_PARTNER_LABELS`, and its logo in `AsSeenOnMeta`.
 */
export const META_PARTNER_AFFILIATE_IDS: Record<MetaPartner, string[]> = {
  trivago: [
    '70059', // AT
    'TrivagoFR',
    '70065', // AU
    '70068', // CA
    '70019', // DE
    '70020', // DK
    'TrivagoES',
    '70013', // FI
    '70063', // HU
    '70057', // IE
    '70058', // NL
    '70004', // NO
    '70066', // NZ
    '70021', // PL
    '70003', // SE
    'Trivagosg', // SG
    '70056', // UK
    'TrivagoPT',
    '70067', // US
    'TrivagoZA', //
    'TrivagoIT',
    'TrivagoTR',
    'TrivagoEurope',
    'TrivagoMemberDE',
    'TrivagoMemberUK',
    'TrivagoMemberES',
    'TrivagoMemberFI',
    'TrivagoMemberUS',
    'TrivagoMemberPT',
  ],
  kayak: [
    '70200', // UK
    '70201', // DE
    '70202', // SE
    '70203', // NO
    '70204', // FI
    '70205', // DK
    '70206', // AT
    '70207', // NZ
    '70208', // NL
    '702011', // AU
    '70210', // PL
    'KayakHotelCA',
    'KayakHotelES',
    'KayakHotelCH',
    'KayakHotelFR',
  ],
};

const META_PARTNERS = Object.keys(META_PARTNER_AFFILIATE_IDS) as MetaPartner[];

/**
 * Resolve the meta partner from an affiliate identity. The partner name (from
 * the `affiliate-name` URL param) wins; otherwise the affiliate id is matched
 * against the per-partner id lists. Returns `undefined` when nothing matches.
 */
export function getMetaPartner(params: {
  affiliateId?: string | null;
  affiliateName?: string | null;
}): MetaPartner | undefined {
  const affiliateName = params.affiliateName?.toLowerCase();
  const nameMatch = META_PARTNERS.find((partner) => partner === affiliateName);
  if (nameMatch) {
    return nameMatch;
  }

  const { affiliateId } = params;
  if (!affiliateId) {
    return undefined;
  }

  return META_PARTNERS.find((partner) =>
    META_PARTNER_AFFILIATE_IDS[partner].includes(affiliateId)
  );
}

/**
 * Resolve the meta partner for the current visit, reading the `affiliate-name`
 * and `affiliate-id` URL params and falling back to the affiliate cookie for the
 * id.
 */
export function resolveMetaPartner(): MetaPartner | undefined {
  const urlParams = new URL(window.location.href).searchParams;
  const affiliateId =
    urlParams.get('affiliate-id') ??
    getAffiliateCookieData()?.affiliateCookieParams.AffiliateId;
  const affiliateName = urlParams.get('affiliate-name');

  return getMetaPartner({ affiliateId, affiliateName });
}
